<script lang="ts">
  import { xhr, toast } from "$app/utils"
  import Honeybadger from "$app/utils/honeybadger"
  import Modal from "$app/components/Modal.svelte"
  import DepartmentForm from "./DepartmentForm.svelte"
  import { SquarePen, Trash2, Menu } from "lucide-svelte"
  import EditDepartmentUsers from "./EditDepartmentUsers.svelte"
  import { appStore } from "$app/store.svelte"

  interface Props {
    department: DepartmentType
    onupdate: (department: DepartmentType) => void
    ondelete: (department: DepartmentType) => void
    onmark: (department: DepartmentType) => void
  }

  let props = $props()
  let { onupdate, ondelete, onmark }: Props = props
  let department = $state(props.department) as DepartmentType

  let saving = false
  let edit = $state(false)
  let editUsers = $state(false)
  let archive = $state(false)

  // Update department
  async function updateDepartment(form: DepartmentType) {
    try {
      saving = true

      const resp = await xhr.put(
        `/plans/${appStore.plan.id}/departments/${department.id}`,
        {
          name: form.name
        }
      )
      department.name = form.name

      onupdate(department)
      toast("Department was updated", "success")
      edit = false
    } catch (e) {
      Honeybadger.notify(e)
      toast("There was an error updating the department", "error")
    } finally {
      saving = false
    }
  }
</script>

{#if edit}
  <DepartmentForm
    {department}
    ondata={(d) => updateDepartment(d)}
    oncancel={() => (edit = false)}
  />
{:else}
  <div class="flex" class:bg-blue-50={archive}>
    <div
      class="px-2 w-8 flex items-center justify-center bg-blue-50 hover:bg-blue-100 border-r border-gray-100"
    >
      <Menu class="size-5 text-gray-500 hover:text-gray-900" />
    </div>
    <div class="px-4 py-3 w-full">
      <div class="flex justify-between items-center">
        <div class="flex-1 whitespace-nowrap text-lg font-medium text-gray-900">
          {department.name}
        </div>
        <div class="whitespace-nowrap text-right">
          <button
            class="text-gray-500 hover:text-blue-500"
            title="Edit"
            onclick={() => (edit = true)}
          >
            <SquarePen class="size-5" />
          </button>
          <button
            type="button"
            class="pl-4 text-yellow-700"
            title="Archive department"
            onclick={() => {
              department._selected = true
              onupdate(department)
              archive = true
            }}
          >
            <Trash2 class="size-5" />
          </button>
        </div>
      </div>

      <div class="flex mt-3">
        <div class="">
          <div class="flex items-center">
            <div>
              {#if department.user_ids.length === 0}
                <div
                  class="text-gray-800 bg-gray-100 py-1 px-2.5 rounded-lg text-sm font-medium"
                >
                  No members
                </div>
              {:else}
                <div
                  class="text-gray-500 bg-indigo-100 px-2.5 py-1 rounded-lg text-sm font-medium flex items-center justify-center"
                >
                  <div
                    class="bg-indigo-800 h-4 w-4 text-white rounded-full flex items-center justify-center mr-1 text-xs"
                  >
                    {department.user_ids.length}
                  </div>
                  {department.user_ids.length === 1 ? "member" : "members"}
                </div>
              {/if}
            </div>

            <div class="ml-3">
              <button
                class="text-blue-500 text-sm cursor-pointer border border-gray-200 rounded-lg py-1 px-3 hover:bg-gray-50 hover:text-blue-600"
                onclick={() => (editUsers = true)}
              >
                {department.user_ids.length === 0
                  ? "Add members"
                  : "Edit members"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
{/if}

<Modal
  bind:open={archive}
  closeButton={true}
  closeOnEsc={false}
  closeOnOuterClick={false}
>
  <div class="p-4 text-center">
    <p>
      Are you sure you want to <span class="font-bold">archive</span> the selected
      department?
    </p>

    <div class="text-yellow-700">
      <svg
        width="80px"
        height="80px"
        class="mx-auto"
        viewBox="0 0 512 512"
        version="1.1"
        xmlns="http://www.w3.org/2000/svg"
        xmlns:xlink="http://www.w3.org/1999/xlink"
      >
        <title>warning-filled</title>
        <g
          id="Page-1"
          stroke="none"
          stroke-width="1"
          fill="none"
          fill-rule="evenodd"
        >
          <g
            id="add"
            fill="#E8C756"
            transform="translate(32.000000, 42.666667)"
          >
            <path
              d="M246.312928,5.62892705 C252.927596,9.40873724 258.409564,14.8907053 262.189374,21.5053731 L444.667042,340.84129 C456.358134,361.300701 449.250007,387.363834 428.790595,399.054926 C422.34376,402.738832 415.04715,404.676552 407.622001,404.676552 L42.6666667,404.676552 C19.1025173,404.676552 7.10542736e-15,385.574034 7.10542736e-15,362.009885 C7.10542736e-15,354.584736 1.93772021,347.288125 5.62162594,340.84129 L188.099293,21.5053731 C199.790385,1.04596203 225.853517,-6.06216498 246.312928,5.62892705 Z M224,272 C208.761905,272 197.333333,283.264 197.333333,298.282667 C197.333333,313.984 208.415584,325.248 224,325.248 C239.238095,325.248 250.666667,313.984 250.666667,298.624 C250.666667,283.264 239.238095,272 224,272 Z M245.333333,106.666667 L202.666667,106.666667 L202.666667,234.666667 L245.333333,234.666667 L245.333333,106.666667 Z"
              id="Combined-Shape"
            />
          </g>
        </g>
      </svg>
      <div class="py-4">
        All related goals and tasks won't be visible once you archive the
        selected department!
      </div>
    </div>

    <div>
      <button
        class="btn btn-primary btn-lg font-semibold"
        onclick={() => {
          ondelete(department)
        }}
      >
        Archive
      </button>
      &nbsp;
      <button
        class="btn btn-secondary btn-lg font-semibold"
        onclick={() => (archive = false)}
      >
        Cancel
      </button>
    </div>
  </div>
</Modal>

<Modal
  bind:open={editUsers}
  closeButton={true}
  closeOnEsc={false}
  closeOnOuterClick={false}
>
  <EditDepartmentUsers
    {department}
    onupdate={(dept) => {
      department = dept
      editUsers = false
    }}
  />
</Modal>
